import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogTitle, IconButton } from '@mui/material';
import { FieldProps, Field as FormikField } from 'formik';
import { Channel } from 'stream-chat/dist/types/channel';

import { updateChannel } from '@common/api/curators';
import Form from '@common/components/Form';
import FileUpload from '@common/components/Form/File';
import TextField from '@common/components/Form/TextField';

interface MassMailingModalProps {
	onClose: () => void;
	chatName: string;
	channel: Channel;
}

interface SimpleModalProps {
	title: string;
	onClose: () => void;
	children: React.ReactNode;
}

const overlaySx = {
	cursor: 'pointer',
	position: 'fixed' as const,
	inset: 0,
	background: 'rgba(0, 0, 0, 0.4)',
	zIndex: 1202,
};

const modalSx = {
	position: 'fixed' as const,
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	background: '#fff',
	borderRadius: '8px',
	width: 'min(520px, calc(100% - 48px))',
	maxHeight: '90vh',
	overflow: 'auto',
	boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
	zIndex: 1203,
};

const headerSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '16px 20px',
	borderBottom: '1px solid #e6e6e6',
};

const contentSx = { padding: '16px 20px' };

function SimpleModal({ title, onClose, children }: SimpleModalProps) {
	const modal = (
		<>
			<Box role="presentation" onClick={onClose} sx={overlaySx} />
			<Box role="dialog" aria-modal="true" aria-label={title} sx={modalSx}>
				<Box sx={headerSx}>
					<DialogTitle sx={{ padding: 0 }} variant="h5">
						{title}
					</DialogTitle>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Box>
				<Box sx={contentSx}>{children}</Box>
			</Box>
		</>
	);

	if (typeof document === 'undefined' || !document.body) {
		return modal;
	}

	return createPortal(modal, document.body);
}

function UpdateChannelModal({ chatName, channel, onClose }: MassMailingModalProps) {
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef<any>(null);

	const handleSubmit = async (data: any) => {
		setIsLoading(true);
		try {
			await updateChannel(channel.cid, { name: data.name, image: data.image?.url ?? '' });
			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<SimpleModal title="Channel settings" onClose={onClose}>
			<Form
				initialValues={{
					name: chatName,
					image: channel?.data?.image ?? '',
				}}
				onSave={handleSubmit}
				loading={isLoading}
				innerRef={formRef}
				actionButtonText="Save"
				formActionsStyles={{ display: 'flex', justifyContent: 'flex-end' }}
			>
				<Box display="flex" flexDirection="column" gap="16px">
					<TextField label="Name" type="text" name="name" />
					<FormikField name="image">
						{({ field, form, meta }: FieldProps) => (
							<FileUpload
								label="Avatar"
								value={field.value}
								onChange={(value) => form.setFieldValue(field.name, value)}
								error={meta.touched ? (meta.error as string) : undefined}
								acceptFiles="IMAGE"
								formatsText="SVG, PNG, JPG or GIF (max. 3MB)"
								showBgImage
								maxSize={3}
							/>
						)}
					</FormikField>
				</Box>
			</Form>
		</SimpleModal>
	);
}

export default UpdateChannelModal;
